import React from 'react';

import { FORM_DATA_KEY_FACTORY, useFormDataQuery } from '@/api/getters';
import {
  useFormMutation,
  useOfferAcceptedMutation,
  useRejectAllOffersMutation,
  useSubmitStepMutation,
} from '@/api/setters';
import { injuryTypeMapper, Stages, Steps } from '@/api/types';
import Flower from '@/assets/flower.svg?react';
import SummaryWrapper from '@/components/layouts/SummaryWrapper';
import Accordion from '@/components/ui/Accordion';
import CarInfo from '@/components/ui/CarInfo';
import InjuryInfo from '@/components/ui/InjuryInfo';
import OfferCards from '@/components/ui/OfferCards';
import StepsComponent from '@/components/ui/Steps/Steps';
import Summary from '@/components/ui/Summary';
import { formMutationDto } from '@/dto/formMutationDto';
import { useStepper } from '@/features/v2/lib/stepper';
import { queryClient } from '@/lib/query';

import { accordionData, steps } from './content';
import { useLingui } from '@lingui/react';

const OldOfferPage = () => {
  const { data } = useFormDataQuery();

  const { i18n } = useLingui();

  const isRejected = data?.offer.offer_rejected ?? false;
  const isAccepted = data?.stage !== Stages.STAGE_III && !isRejected;

  const summary = {
    surcharge: data?.offer.surcharge ?? 0,
    tax: data?.offer.tax ?? 0,
    total: data?.offer.surcharge_summary ?? 0,
  };

  const acceptOfferMutation = useOfferAcceptedMutation({ isHouseHold: false });
  const rejectOfferMutation = useRejectAllOffersMutation({
    isHouseHold: false,
  });
  const formMutation = useFormMutation();
  const { setStep, setStepTag, stepTag, innerObject, step } = useStepper();
  const submitStep = useSubmitStepMutation();

  const handleAcceptOffer = async (hash: string) => {
    if (!data) return;
    await formMutation.mutateAsync({
      ...formMutationDto({
        form_data: data?.payload?.data ?? null,
        stepper: {
          step: data?.offer.is_complementary ? 4 : 5,
          innerStep: { ...innerObject, [step + 1]: 0 },
        },
      }),
    });

    await acceptOfferMutation.mutateAsync(hash, {
      onSuccess: () => {
        setStep(data.offer.is_complementary ? 4 : 5);
        queryClient.invalidateQueries({
          queryKey: FORM_DATA_KEY_FACTORY(hash),
        });
        if (stepTag === Steps.OFFER && !data.offer.is_complementary) {
          submitStep.mutateAsync(Steps.TRANSFER_DETAILS).then(() => {
            setStepTag(Steps.TRANSFER_DETAILS);
          });
        }
        if (data.offer.is_complementary) {
          submitStep.mutateAsync(Steps.COMPLEMENTARY_FORM).then(() => {
            setStepTag(Steps.COMPLEMENTARY_FORM);
          });
        }
      },
    });
  };

  const handleRejectOffer = async () => {
    await rejectOfferMutation.mutateAsync();
  };

  const injuryInfo = {
    insuranceCompany:
      data?.product.additional_info.insurance_policy?.insurance_policy ?? '-',
    insuranceLogo:
      data?.product.additional_info.insurance_policy?.insurance_logo ?? '-',
    injuryNumber:
      data?.product.additional_info.insurance_policy?.damage_number ?? '-',
    injuryDate:
      data?.product.additional_info.insurance_policy?.damage_date ?? undefined,
    insuranceType:
      data?.product.additional_info.insurance_policy?.insurance_type ?? '-',
    injuryType: data?.offer?.type_of_damage
      ? injuryTypeMapper[data?.offer?.type_of_damage]
      : '-',
    valueOfInjury:
      Number(
        data?.product.additional_info.insurance_policy
          ?.value_according_to_insurer
      ) ?? 0,
    tax: `${(data?.offer?.income_tax ?? 0) * 100}%`,
  };

  const carInfo = {
    carMake: data?.product.additional_info.car ?? '-',
    carYear: data?.product.additional_info.car_year ?? '-',
    carLogo: data?.product.additional_info.car_logo ?? '-',
    carModel: data?.product.additional_info.car_model ?? '-',
    plateNumber: data?.product.additional_info.car_plate || '-',
    vinNumber: data?.product.additional_info.vin ?? '-',
    wheelSide:
      data?.product.additional_info.wheel_side === 'right'
        ? i18n.t('Po prawej')
        : i18n.t('Po lewej'),
    accidentWasInPoland: data?.product.additional_info.accident_in_poland
      ? i18n.t('Tak')
      : i18n.t('Nie'),
  };

  const date = new Date(
    data?.offer?.opened_at ?? new Date(new Date().getTime() - 60000)
  );

  date.setDate(date.getDate() + 1);
  const deadlineDate = date.toISOString();

  const SummaryComponent = (
    <Summary
      heading={
        isAccepted ? (
          <>
            <span className="max-w-[500px] truncate">{`${i18n.t('Świetnie,')} ${data?.user?.name ?? '-'}!`}</span>
            <span>🎉</span>
          </>
        ) : (
          <span>{i18n.t('Nasza propozycja')}</span>
        )
      }
      subheading={
        isAccepted && (
          <>
            <Flower />
            {i18n.t('Propozycja została zaakceptowana')}
          </>
        )
      }
      withButtons={!isAccepted}
      {...summary}
      onClickAccept={() => handleAcceptOffer(data?.offer.hash ?? '')}
      loadingAccept={acceptOfferMutation.isPending}
      onClickReject={handleRejectOffer}
      loadingReject={rejectOfferMutation.isPending}
    />
  );
  return (
    <>
      <SummaryWrapper>
        {SummaryComponent}
        <OfferCards
          date={deadlineDate}
          isAccepted={isAccepted}
          tax={summary.tax ?? 0}
        />
        <InjuryInfo {...injuryInfo} />
        <CarInfo {...carInfo} />
      </SummaryWrapper>

      <StepsComponent
        steps={steps}
        title={i18n.t('Kolejne kroki po zaakceptowaniu propozycji')}
        caption={i18n.t('Co dalej?')}
      />
      {/* commented for now it will be updated later */}
      {/* <Chart
        caption={i18n.t('Sprawdź nasze wyniki')}
        headline={i18n.t('Zobacz, jak pomogliśmy naszym Klientom')}
        bars={chartBars}
      /> */}
      <div className="max-w-[980px] rounded-lg border-t-[6px] border-blue-600 bg-gray-100 px-3.5 py-4 lg:rounded-[10px] lg:px-10 lg:pb-10 lg:pt-[34px]">
        <div className="grid grid-cols-1 gap-x-2.5 gap-y-5 lg:grid-cols-2 lg:gap-x-5 lg:gap-y-[30px]">
          {SummaryComponent}
          <OfferCards
            date={deadlineDate}
            isAccepted={isAccepted}
            tax={summary.tax ?? 0}
          />
        </div>
      </div>
      <Accordion data={accordionData} />
    </>
  );
};

export default OldOfferPage;
